import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import VehiclesPropertyGroup from '../../../components/PropertyGroup/VehiclesPropertyGroup';
import ListingSection from '../ListingSection';
import ListingSectionItem from '../ListingSectionItem';

/**
 * This component is used to render the vehicles section of the listing page.
 * @param {object} props
 * @param {object} props.options - Options for the vehicles property group.
 * @param {Array<string>} props.kfz - Selected vehicles.
 * @param {string} props.allWheelDrive - Whether all-wheel drive is needed.
 * @returns {React.JSX.Element|null}
 */

const SectionVehiclesMaybeComponent = ({ options, kfz = [], allWheelDrive = 'no' }) => {
  // Early return if publicData is falsy
  if (!kfz && !allWheelDrive) {
    return null;
  }

  // Simplify selectedOptions retrieval
  const selectedOptions = kfz ?? [];

  // Determine allWheelDriveOption more concisely
  const allWheelDriveNeeded = allWheelDrive === 'yes';

  // Render null if no kfz data and no allWheelDriveOption
  if (selectedOptions.length <= 0 && !allWheelDriveNeeded) {
    return null;
  }

  return (
    <ListingSection>
      <ListingSectionItem
        params={{
          sx: { justifyContent: { xs: 'center', md: 'flex-start' } },
        }}
      >
        <Typography
          component="h3"
          mb={2}
          sx={{ textAlign: { xs: 'center', md: 'left' } }}
          variant="h4"
        >
          <FormattedMessage id="ListingPage.kfzTitle" />
        </Typography>
      </ListingSectionItem>
      <ListingSectionItem
        params={{
          sx: { textAlign: { xs: 'center', md: 'left' } },
        }}
      >
        <VehiclesPropertyGroup
          options={options}
          selectedOptions={selectedOptions}
          showNotSelectedItems={false}
          twoColumns={true}
        />
      </ListingSectionItem>
      {allWheelDriveNeeded ? (
        <ListingSectionItem>
          <Chip
            icon={<InfoOutlinedIcon />}
            label={
              <Typography variant="body2">
                <FormattedMessage id="ListingPage.allWheelDriveInfo" />
              </Typography>
            }
            sx={{
              '&.MuiChip-root': { height: 'auto' },
            }}
            variant="info"
          />
        </ListingSectionItem>
      ) : null}
    </ListingSection>
  );
};

SectionVehiclesMaybeComponent.displayName = 'SectionVehiclesMaybe';

const SectionVehiclesMaybe = memo(SectionVehiclesMaybeComponent);

export default SectionVehiclesMaybe;
